import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Lock, RefreshCw, CheckCircle, Send } from 'lucide-react';
import salesTrendsLogo from '@/assets/sales-trends-logo.png';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { email?: string; showResend?: boolean } | null;

  // Pre-fill email from state
  useState(() => {
    if (state?.email && !email) {
      setEmail(state.email);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        title: 'Login failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    // Check user role: global admin > loan officer > site admin (agent)
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setLoading(false);
      return;
    }

    // Check if global admin
    const { data: globalAdmin } = await supabase
      .from('global_admins')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (globalAdmin) {
      toast({ title: 'Welcome back!' });
      navigate('/admin');
      setLoading(false);
      return;
    }

    // Check if loan officer
    const { data: loanOfficer } = await supabase
      .from('loan_officers')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (loanOfficer) {
      toast({ title: 'Welcome back!' });
      navigate('/loanofficer');
      setLoading(false);
      return;
    }

    // Default: site admin (agent)
    toast({ title: 'Welcome back!' });
    navigate('/agent');
    setLoading(false);
  };

  const handleResendCredentials = async () => {
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address first.',
        variant: 'destructive',
      });
      return;
    }

    setResending(true);

    try {
      const { data, error } = await supabase.functions.invoke('resend-credentials', {
        body: { 
          email: email.trim().toLowerCase(),
          loginUrl: window.location.origin + '/admin/login',
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResendSuccess(true);
      toast({
        title: 'Credentials sent!',
        description: 'Check your email for your login credentials.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend credentials. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address first.',
        variant: 'destructive',
      });
      return;
    }

    setSendingReset(true);

    try {
      const { data, error } = await supabase.functions.invoke('request-password-reset', {
        body: { 
          email: email.trim().toLowerCase(),
          resetUrl: window.location.origin + '/admin/reset-password',
        },
      });

      if (error) throw error;

      setResetSent(true);
      toast({
        title: 'Reset email sent!',
        description: 'Check your email for a password reset link.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reset email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8">
          <img src={salesTrendsLogo} alt="Sales Trends Network" className="h-16 w-auto" />
          <span className="text-primary-foreground font-display text-xl">Sales Trends Digital Real Estate</span>
        </Link>
        
        <Card className="shadow-elegant">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl">Admin Login</CardTitle>
            <CardDescription>Sign in to manage your real estate site</CardDescription>
          </CardHeader>
          <CardContent>
            {resendSuccess && (
              <div className="mb-4 p-4 bg-estate-success/10 border border-estate-success/30 rounded-lg flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-estate-success mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-estate-success">Credentials Sent!</p>
                  <p className="text-sm text-muted-foreground">Check your email for your login credentials.</p>
                </div>
              </div>
            )}
            
            {(state?.showResend && !resendSuccess) && (
              <div className="mb-4 p-4 bg-accent/10 border border-accent/30 rounded-lg">
                <p className="text-sm mb-3">Didn't receive your credentials? Enter your email and click resend.</p>
                <Button
                  variant="gold"
                  size="sm"
                  className="w-full"
                  onClick={handleResendCredentials}
                  disabled={resending}
                >
                  {resending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Resend Credentials
                    </>
                  )}
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" /> Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button type="submit" variant="gold" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-border text-center space-y-3">
              {resetSent ? (
                <div className="flex items-center justify-center gap-2 text-sm text-estate-success">
                  <CheckCircle className="h-4 w-4" />
                  Reset link sent! Check your email.
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-muted-foreground hover:text-accent transition-colors flex items-center justify-center gap-2 mx-auto"
                  disabled={sendingReset}
                >
                  {sendingReset ? (
                    <>
                      <Send className="h-3 w-3 animate-pulse" />
                      Sending reset link...
                    </>
                  ) : (
                    "Forgot your password?"
                  )}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
